import { useCallback, useEffect, useState } from 'react';
import { useSetImmerAtom } from 'jotai-immer';
import { cpOrderInvoicesPageStateAtom } from '../cp_order_invoices_list_atoms';
import { useCpOrderInvoicesListDestroy } from '../table/useCpOrderInvoicesListDestroy';
import utilSvc from '../../../../core/utilSvc';
import { PreloadState } from '../../../../core/page/page.types';
import { ValidOption } from '../../../../core/components/validation/types';
import { ngContext } from '../../../../core/services/ng.delegate';
import { getAffs } from '../../cp_order_invoices_const';

export const programAdminRenameMap: Record<string, string> = {
    PSG: 'OMNICELL',
    MORRISDICKSON: 'CERVEY',
    '340BASICS': 'NUVEM',
    SENTRY: 'CRANEWARE',
};

export const amendProgramAdminOptions = (options: string[]): ValidOption[] => {
    return options.map(opt => {
        return { val: opt, lbl: programAdminRenameMap[opt] ?? opt };
    });
};

export type CpOrderInvoicesOptions = {
    ceOptions: ValidOption[];
    rxOptions: ValidOption[];
    programAdminOptions: ValidOption[];
};

// Extracted option-building logic
export function buildCpOrderInvoicesOptions(ctx: any): CpOrderInvoicesOptions {
    // Get affiliations from user context
    const affs = getAffs(ctx.User?.AffList, 'invoice.cp');

    // Build CE and Program Admin options
    const ceList = utilSvc.aff.ceList(affs);
    const rxList = utilSvc.aff.rxList(affs);
    const progAdminList = utilSvc.aff.findProgramAdmin(affs);

    // Convert to option format: { lbl, val }
    const ceDefault = [{ lbl: 'Select a Facility', val: 'N/A' }];
    const ceOptions = ceList.map(ce => ({ lbl: ce, val: ce }));
    const ceAllOptions = ceDefault.concat(ceOptions);

    const rxOptions = rxList.map(rx => ({ lbl: rx, val: rx }));

    const progAdminDefault = [{ lbl: 'Select a Program Admin', val: 'N/A' }];
    const progAdminOptions = amendProgramAdminOptions(progAdminList);
    const sortedProgAdminOptions = utilSvc.array.sortTextASC([...progAdminOptions], 'lbl');
    const progAdminAllOptions = progAdminDefault.concat(sortedProgAdminOptions);

    return {
        ceOptions: ceAllOptions,
        rxOptions,
        programAdminOptions: progAdminAllOptions,
    };
}

/**
 * IMPORTANT:
 * Do not add `ctx` as a dependency in any of the useEffects
 * inside of this hook!
 * */
export const useCpOrderInvoicesInit = (): boolean => {
    const [preloadState, setPreloadState] = useState<PreloadState>('init');
    const setPageState = useSetImmerAtom(cpOrderInvoicesPageStateAtom);

    useCpOrderInvoicesListDestroy();

    useEffect(() => {
        // Read context imperatively at mount time; capture all user data for downstream use
        const ctx = ngContext();
        setPageState(draft => {
            draft.canEdit = ctx.User?.Type === 'VERITYSA';
            draft.userType = ctx.User?.Type ?? '';
            draft.userAffList = ctx.User?.AffList ?? [];
            draft.userImpersonate = !!ctx.User?.Impersonate;
            return draft;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const preloadPage = useCallback(async () => {
        setPageState(draft => {
            draft.init = false;
            draft.loading = false;
            draft.action = 'QUERY';
            return draft;
        });

        setPreloadState('done');
    }, [setPageState]);

    useEffect(() => {
        if (preloadState === 'init') {
            setPreloadState('loading');

            // ASYNC preload
            setPageState(draft => {
                draft.init = true;
                draft.loading = true;
                return draft;
            });

            preloadPage();
        }
    }, [preloadState, setPreloadState, setPageState, preloadPage]);

    return preloadState === 'done';
};
